import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { IconBell } from '../icons';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { ago } from '../schedule/crewAsOf';
import { useNow } from '../ui/useNow';
import type { BellNotification } from '../tickets/types';

/**
 * The bell (ops insights Phase 5a, ticket 05; spec §5): one person's own notifications
 * (CONTEXT.md "Notification"). Never the alerts board itself: since Phase 7 only an alert
 * assigned to them, or escalated to them, which the server shows to the owner and the office
 * alone.
 *
 *  - An unread badge on the trigger ("9+" past nine, nothing at none), and the trigger SAYS the
 *    count ("3 unread notifications"), because the badge is drawn, not read.
 *  - The count keeps itself current only while it can be seen: every 60 s while the browser
 *    tab is visible, and again when the tab comes back. Both are TanStack's focus manager,
 *    which watches `visibilitychange` alone: "focus" there means the TAB becoming visible, not
 *    the window gaining focus (`refetchIntervalInBackground: false`, `refetchOnWindowFocus`).
 *    The crew map's pattern (`SchedulePage`). Where a shell mounts two bells (the office's
 *    sidebar and phone bar, one hidden by the viewport), only the one shown polls (`polls`).
 *  - The list is asked for every time the popover opens, never served from the cache alone:
 *    `PopoverContent` mounts `BellPanel` on open, and its pages refetch on mount whatever the
 *    app's 30 s `staleTime` says, so a notification the badge already counts is in the list.
 *  - On open the focus goes to the first notification, or to the heading while the list
 *    loads (then on to the first notification, if it is still there): never to "Mark all
 *    read", which Radix's own first-tabbable rule would pick.
 *  - Each notification is a link to where the SERVER says it opens (`opens`): the Tickets
 *    page with the panel open for the office, the visit page of that ticket's job for a tech.
 *    Opening one marks it read. "Mark all read" marks the rest.
 *
 * ⚠️ The focus is handed back to the trigger by hand on every close (Escape, a click outside,
 * a link). Radix's own restore was measured landing on <body> under React 19.1 (the drawer's
 * and `Sheet`'s same fix), and a link must leave the focus on the trigger BEFORE the page it
 * opens draws its panel: `Sheet` takes whatever is focused at that render as its opener.
 */

/** How often the count is asked again while the tab is visible. */
export const BELL_POLL_MS = 60_000;
/** One page of the list. */
export const BELL_PAGE = 20;

/** What the trigger says: the count in words, for the badge a screen reader cannot see. */
export function bellLabel(unread: number | undefined): string {
  if (unread === undefined) return 'Notifications';
  if (unread === 0) return 'No unread notifications';
  return unread === 1 ? '1 unread notification' : `${unread} unread notifications`;
}

/** The badge's text: nothing at none, the count up to nine, then "9+". */
export function badgeText(unread: number | undefined): string | null {
  if (!unread || unread <= 0) return null;
  return unread > 9 ? '9+' : String(unread);
}

/** A notification about a ticket (`AlertBellNotification`: about an alert, Phase 7). */
type TicketBellNotification = Extract<BellNotification, { ticket: unknown }>;
type AlertBellNotification = Extract<BellNotification, { alert: unknown }>;

/** "#42 Leaking valve". */
function ticketName(n: TicketBellNotification): string {
  return `#${n.ticket.number} ${n.ticket.title}`;
}

/**
 * The plain sentence a notification reads as. Someone no longer in the shop is "Former staff"
 * (the spec's word for a deleted author); a change nobody made reads without a subject.
 */
export function notificationSentence(n: BellNotification): string {
  const who = n.actor ? (n.actor.name ?? 'Former staff') : null;
  if ('alert' in n) return alertSentence(n, who);
  const what = ticketName(n);
  switch (n.kind) {
    case 'ticket_assigned':
      return who ? `${who} assigned you ${what}` : `You were assigned ${what}`;
    case 'ticket_raised':
      // A customer's report has no actor (the form writes as the system): it is theirs.
      if (n.ticket.source === 'customer') return `A customer reported a problem: ${what}`;
      return who ? `${who} flagged a problem: ${what}` : `A problem was flagged: ${what}`;
    case 'ticket_message':
      return who ? `New message from ${who} on ${what}` : `New message on ${what}`;
  }
}

/** An alert assigned to the reader, or escalated to them (Phase 7 spec §8). */
function alertSentence(n: AlertBellNotification, who: string | null): string {
  switch (n.kind) {
    case 'alert_assigned':
      return who
        ? `${who} assigned you an alert: ${n.alert.label}`
        : `You were assigned an alert: ${n.alert.label}`;
    case 'alert_escalated':
      // The sweep escalates it: nobody did, so there is no one to name.
      return `Nobody has taken a ${n.alert.band} alert: ${n.alert.label}`;
  }
}

/** "Just now", "5 min ago", "3 h ago", "2 days ago". */
export function whenLabel(at: Date | string, now: number): string {
  const ms = now - new Date(at).getTime();
  return ms < 60_000 ? 'Just now' : ago(ms);
}

/**
 * The bell's trigger and its popover. `side`/`align` place the popover by where the bell sits:
 * DOWN from a top bar on a phone, to the RIGHT on a desktop sidebar. `besideRef` names the box
 * the popover opens beside instead of the bell itself: the sidebar's header, so the popover
 * clears the whole sidebar (its collapse button included), expanded or collapsed to icons, and
 * covers no control it came from. `clearsTabBar` keeps a phone's popover above the office's
 * bottom tab bar, which it must never cover. `polls` is false for a bell its shell has hidden
 * for this viewport: it still draws the shared count, and leaves the polling to the shown one.
 */
export function Bell({
  side = 'bottom',
  align = 'end',
  besideRef,
  clearsTabBar = false,
  polls = true,
  className = '',
}: {
  side?: 'bottom' | 'right';
  align?: 'start' | 'end';
  besideRef?: RefObject<HTMLElement | null>;
  clearsTabBar?: boolean;
  polls?: boolean;
  className?: string;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const count = useQuery(
    trpc.notification.unreadCount.queryOptions(undefined, {
      refetchInterval: polls ? BELL_POLL_MS : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: polls ? 'always' : false,
    }),
  );
  const unread = count.data?.unread;
  const badge = badgeText(unread);

  /** Leave for a notification: the focus goes home first, then the popover closes. */
  const leave = () => {
    trigger.current?.focus();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {besideRef ? <PopoverAnchor virtualRef={besideRef} /> : null}
      <PopoverTrigger asChild>
        <button
          ref={trigger}
          type="button"
          data-bell=""
          data-bell-polls={polls ? 'true' : 'false'}
          data-bell-anchor={besideRef ? 'beside' : 'trigger'}
          aria-label={bellLabel(unread)}
          className={`relative inline-flex shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-muted-foreground hover:bg-surface-2 hover:text-text ${className}`}
        >
          <IconBell size={18} />
          {badge ? (
            <span
              aria-hidden="true"
              data-bell-badge=""
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold leading-none text-ink"
            >
              {badge}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={side === 'right' ? 8 : 6}
        collisionPadding={8}
        aria-label="Notifications"
        // The panel places the focus itself (`BellPanel`): Radix's first tabbable is "Mark all
        // read", one press from marking everything.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          trigger.current?.focus();
        }}
        style={{
          maxHeight: clearsTabBar
            ? 'min(32rem, calc(var(--radix-popover-content-available-height) - var(--sw-tabbar-h) - var(--sw-tabbar-border) - var(--sw-safe-bottom)))'
            : 'min(32rem, var(--radix-popover-content-available-height))',
        }}
        className="flex w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden border-border bg-surface p-0 text-text shadow-[var(--shadow-lift)]"
      >
        <BellPanel onLeave={leave} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * What the open popover shows: the person's notifications, newest first, a page at a time
 * ("Show more"), each a link that marks it read; and "Mark all read". Mounted only while open,
 * so the list is asked for then and not before.
 */
export function BellPanel({ onLeave }: { onLeave: () => void }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const now = useNow();
  const [pages, setPages] = useState(1);
  const list = useRef<HTMLUListElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  /** The focus has been placed once, on open. */
  const placed = useRef(false);
  /** The row to focus once "Show more"'s page is drawn (the button may be gone by then). */
  const focusRow = useRef<number | null>(null);

  const results = useQueries({
    queries: Array.from({ length: pages }, (_, i) =>
      trpc.notification.list.queryOptions(
        { limit: BELL_PAGE, offset: i * BELL_PAGE },
        // Asked again on every open: the app's 30 s `staleTime` would otherwise draw a cached
        // list without the notification the badge has just counted.
        { staleTime: 0, refetchOnMount: 'always' },
      ),
    ),
  });
  const first = results[0]!;
  const loaded = results.every((r) => r.data !== undefined);
  const seen = new Set<string>();
  // Paged by offset over a list that grows at the top, so a row can arrive twice: once.
  const rows = results
    .flatMap((r) => r.data?.rows ?? [])
    .filter((n) => (seen.has(n.id) ? false : (seen.add(n.id), true)));
  const total = results.at(-1)?.data?.total ?? first.data?.total ?? 0;
  const more = loaded && pages * BELL_PAGE < total;
  // The trigger's count (one cached read, no second request) as well as the rows drawn: an
  // unread one can sit past the pages loaded.
  const count = useQuery(
    // Not a second poller: the trigger's bell polls this read.
    trpc.notification.unreadCount.queryOptions(undefined, {
      refetchOnWindowFocus: false,
    }),
  );
  const anyUnread = (count.data?.unread ?? 0) > 0 || rows.some((n) => n.readAt === null);

  const refresh = () => qc.invalidateQueries(trpc.notification.pathFilter());
  const markRead = useMutation(
    trpc.notification.markRead.mutationOptions({ onSettled: refresh }),
  );
  const markAll = useMutation(
    trpc.notification.markAllRead.mutationOptions({ onSettled: refresh }),
  );

  // The focus on open: the first notification when the list is drawn, else the heading; and
  // when a list that was loading lands, on to the first notification if the focus is still
  // on the heading (someone who has moved on keeps theirs).
  const listDrawn = first.data !== undefined;
  useEffect(() => {
    const item = list.current?.querySelectorAll<HTMLElement>('[data-bell-item]')[0];
    if (!placed.current) {
      placed.current = true;
      (item ?? heading.current)?.focus({ preventScroll: true });
    } else if (item && document.activeElement === heading.current) {
      item.focus({ preventScroll: true });
    }
  }, [listDrawn]);

  useEffect(() => {
    if (focusRow.current === null || !loaded) return;
    const links = list.current?.querySelectorAll<HTMLElement>('[data-bell-item]');
    links?.[focusRow.current]?.focus();
    focusRow.current = null;
  }, [loaded, rows.length]);

  const open = (n: BellNotification) => {
    if (n.readAt === null) markRead.mutate({ ids: [n.id] });
    onLeave();
  };

  return (
    <>
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2">
        <h2
          ref={heading}
          tabIndex={-1}
          className="rounded-sm font-heading text-sm font-semibold focus:outline-3 focus:outline-offset-2 focus:outline-brand focus:outline-solid"
        >
          Notifications
        </h2>
        {/* Kept while there is a list, and only DISABLED once nothing is unread: a button
            that removed itself on its own success would drop the focus to <body>
            (LESSONS 2026-10-05). */}
        {rows.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            aria-disabled={!anyUnread || markAll.isPending || undefined}
            onClick={() => {
              if (anyUnread && !markAll.isPending) markAll.mutate();
            }}
          >
            Mark all read
          </Button>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {first.isPending ? (
          <div className="space-y-2 p-3" aria-busy="true">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : first.isError ? (
          <div className="space-y-2 p-3 text-sm">
            <p>Couldn’t load your notifications.</p>
            <Button variant="ghost" size="sm" onClick={() => void first.refetch()}>
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            Nothing yet. You’ll hear here when a ticket is assigned to you, a tech flags a
            problem, or someone writes on a ticket you follow.
          </p>
        ) : (
          <ul ref={list} className="divide-y divide-border">
            {rows.map((n) => (
              <li key={n.id}>
                <NotificationLink n={n} onOpen={() => open(n)}>
                  <span
                    aria-hidden="true"
                    className={`mt-1.5 size-2 shrink-0 rounded-full ${n.readAt === null ? 'bg-brand' : 'bg-transparent'}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-sm ${n.readAt === null ? 'font-semibold' : ''}`}
                    >
                      {n.readAt === null ? (
                        <span className="sr-only">Unread: </span>
                      ) : null}
                      {notificationSentence(n)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {whenLabel(n.createdAt, now)}
                    </span>
                  </span>
                </NotificationLink>
              </li>
            ))}
          </ul>
        )}
      </div>
      {more ? (
        <div className="shrink-0 border-t border-border p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => {
              focusRow.current = rows.length;
              setPages((p) => p + 1);
            }}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </>
  );
}

const itemClass =
  'flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-surface-2 focus-visible:bg-surface-2';

/** A left click with no modifier: the one the app follows itself (a Link's own rule). */
const plainClick = (e: MouseEvent) =>
  !e.defaultPrevented &&
  e.button === 0 &&
  !e.metaKey &&
  !e.altKey &&
  !e.ctrlKey &&
  !e.shiftKey;

/**
 * The link one notification is, to where the server says it opens. The office's lands on the
 * Tickets page with that ticket's panel open and its row marked; a tech's on the visit of that
 * ticket's job, at the ticket (`#ticket-<id>`); an alert's on the Alerts page with that alert's
 * panel open (`/alerts?alert=<id>`).
 */
function NotificationLink({
  n,
  onOpen,
  children,
}: {
  n: BellNotification;
  onOpen: () => void;
  children: ReactNode;
}) {
  const router = useRouter();
  const common = {
    'data-bell-item': n.id,
    className: itemClass,
    onClick: onOpen,
  } as const;
  if ('alert' in n) {
    // An address, not a typed `<Link to="/alerts">`: the board's route (ops insights Phase 7
    // ticket 02) validates `?alert=` itself, and this link must not depend on it being
    // registered here. A plain click is followed in the app, as a Link's is; a modified one
    // (a new tab) is the browser's.
    const href = `/alerts?alert=${encodeURIComponent(n.opens.alertId)}`;
    return (
      <a
        href={href}
        {...common}
        onClick={(e) => {
          onOpen();
          if (!plainClick(e)) return;
          e.preventDefault();
          void router.navigate({ href });
        }}
      >
        {children}
      </a>
    );
  }
  switch (n.opens.page) {
    case 'tickets':
      return (
        <Link to="/tickets" search={{ ticket: n.ticket.id }} {...common}>
          {children}
        </Link>
      );
    case 'visit':
      return (
        <Link
          to="/tech/visits/$visitId"
          params={{ visitId: n.opens.visitId }}
          hash={`ticket-${n.ticket.id}`}
          {...common}
        >
          {children}
        </Link>
      );
    case 'day':
      return (
        <Link to="/tech" {...common}>
          {children}
        </Link>
      );
  }
}
